import React, { useEffect, useState, useRef } from 'react';
import {
  withStyles, FormControl, FormLabel, FormGroup, Checkbox,
} from '@material-ui/core';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import {
  Formik, Form, Field, ErrorMessage,
} from 'formik';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import styles from '../styles';
import Footer from '~/pages/Manager/Footer';
import PF2MTable from '~/components/PF2MTable';
import {
  getFireIds,
  addFireId,
  setFireIdValue,
  resetFireIds,
  updateFireIds,
  getElements,
  getSubElements,
} from '~/store/manager/actions';
import PF2MSearchSelectOutlined from '~/components/PF2MSearchSelectOutlined';
import PF2MAddButton from '~/components/PF2MAddButton';
import { sortByProperties } from '~/utils';

const FireIdTab = ({ classes }) => {
  const fireIds = useSelector(state => state.manager.fireIds);
  const elements = useSelector(state => state.manager.elements);
  const subElements = useSelector(state => state.manager.subElements);
  const isFireIdsDirty = useSelector(state => state.manager.isFireIdsDirty);
  const dispatch = useDispatch();
  const [selectedElement, setSelectedElement] = useState(0);
  const [selectedSubElement, setSelectedSubElement] = useState(0);
  const [fireIdInactive, setFireIdInactive] = useState(false);
  const formikRef = useRef();
  const { t: translate } = useTranslation();

  useEffect(() => {
    async function fetchData() {
      dispatch(getFireIds(fireIdInactive));
      dispatch(getElements());
      dispatch(getSubElements());
    }
    fetchData();
  }, [dispatch, fireIdInactive]);

  const columns = [
    { field: 'origin_name', title: translate('common:Element') },
    { field: 'point_name', title: translate('common:SubElement') },
    {
      field: 'fire_id',
      title: translate('common:FireID'),
      editable: true,
      width: '150px',
      updateOnChange: true,
      type: 'number',
    },
    {
      field: 'name',
      title: translate('common:FireDescription'),
      editable: true,
      updateOnChange: true,
    },
    {
      field: 'active',
      title: 'STATUS',
      editable: true,
    },
  ];

  const renderHeader = () => (
    <Formik
      ref={formikRef}
      initialValues={{
        element: selectedElement,
        subElement: selectedSubElement,
        name: '',
        fireId: '',
      }}
      validate={(values) => {
        const errors = {};
        const {
          element,
          subElement,
          name,
          fireId,
        } = values;

        if (!name) {
          errors.name = translate('validation:RequiredField');
        }

        if (!fireId) {
          errors.fireId = translate('validation:RequiredField');
        }

        if (element <= 0 && name) {
          errors.element = translate('validation:RequiredField');
        }

        if (subElement === 0 && name) {
          errors.subElement = translate('validation:RequiredField');
        }

        return errors;
      }}
      onSubmit={async (values, { resetForm }) => {
        const element = elements.find(t => t.id === values.element);
        const subElement = subElements.find(t => t.id === values.subElement);
        const newValues = {
          origin_id: element.id,
          origin_name: element.name,
          point_id: subElement.id,
          point_name: subElement.name,
          name: values.name,
          fire_id: values.fireId,
        };
        dispatch(addFireId(newValues));
        resetForm();
      }}
    >
      {({
        isSubmitting,
        errors,
        isValid,
      }) => (
        <Form>
          <div style={{ display: 'flex', marginBottom: 15, minHeight: 75 }}>
            <FormControl>
              <FormLabel className={classes.formLabel}>
                {`${translate('common:Element')}:`}
              </FormLabel>
              <FormGroup>
                <Field
                  name="element"
                  render={({ field, form }) => (
                    <PF2MSearchSelectOutlined
                      {...field}
                      placeholder={null}
                      className={classnames(
                        form.errors[field.name] ? classes.fieldError : classes.field,
                      )}
                      onChange={async (e) => {
                        form.setFieldValue('element', e.target.value);
                        setSelectedElement(e.target.value);
                      }}
                    >
                      {[{ value: 0, label: translate('common:Select') }]
                        .concat(...elements
                          .map(e => ({ value: e.id, label: e.name })))}
                    </PF2MSearchSelectOutlined>
                  )}
                />
                <ErrorMessage
                  name="element"
                  component="span"
                  className={classes.errorMessage}
                />
              </FormGroup>
            </FormControl>
            <FormControl style={{ marginLeft: 10 }}>
              <FormLabel className={classes.formLabel}>
                {`${translate('common:SubElement')}:`}
              </FormLabel>
              <FormGroup>
                <Field
                  name="subElement"
                  render={({ field, form }) => (
                    <PF2MSearchSelectOutlined
                      {...field}
                      placeholder={null}
                      className={classnames(
                        form.errors[field.name] ? classes.fieldError : classes.field,
                      )}
                      onChange={async (e) => {
                        form.setFieldValue('subElement', e.target.value);
                        setSelectedSubElement(e.target.value);
                      }}
                    >
                      {[{ value: 0, label: translate('common:Select') }]
                        .concat(...subElements
                          .filter(s => s.id_element === selectedElement)
                          .map(e => ({ value: e.id, label: e.name })))}
                    </PF2MSearchSelectOutlined>
                  )}
                />
                <ErrorMessage
                  name="subElement"
                  component="span"
                  className={classes.errorMessage}
                />
              </FormGroup>
            </FormControl>
            <FormControl style={{ marginLeft: 10 }}>
              <FormLabel className={classes.formLabel}>
                {`${translate('common:FireID')}:`}
              </FormLabel>
              <FormGroup>
                <Field
                  type="text"
                  name="fireId"
                  className={classnames('form-control', errors.name ? classes.fieldError : classes.field)}
                  style={{ width: '150px' }}
                />
                <ErrorMessage
                  name="fireId"
                  component="span"
                  className={classes.errorMessage}
                />
              </FormGroup>
            </FormControl>
            <FormControl className={classes.classGroupName}>
              <FormLabel className={classes.formLabel}>
                {`${translate('common:FireDescription')}:`}
              </FormLabel>
              <FormGroup>
                <Field
                  type="text"
                  name="name"
                  className={classnames('form-control', errors.name ? classes.fieldError : classes.field)}
                  style={{ width: '100%' }}
                />
                <ErrorMessage
                  name="name"
                  component="span"
                  className={classes.errorMessage}
                />
              </FormGroup>
            </FormControl>
            <div style={{ marginTop: 12 }}>
              <PF2MAddButton disabled={isSubmitting || !isValid} />
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );

  return (
    <div className={classes.tabContainer}>
      {renderHeader()}
      <div style={{ display: 'flex', marginBottom: 1, minHeight: 5 }}>
        <div>
          <Checkbox checked={fireIdInactive} onChange={() => setFireIdInactive(!fireIdInactive)} />
        </div>
        <div>
          <p>{translate('common:ShowInactive')}</p>
        </div>
      </div>
      <PF2MTable
        data={sortByProperties(
          fireIds
            .filter(c => (selectedElement === 0 || c.origin_id === selectedElement)
              && (selectedSubElement === 0 || c.point_id === selectedSubElement)),
          [
            { prop: 'origin_name', type: 'string' },
            { prop: 'point_name', type: 'string' },
            { prop: 'fire_id', type: 'number' },
          ],
        )}
        columns={columns}
        updateItem={(row, field, value) => dispatch(setFireIdValue(row, field, value))}
      />
      <Footer
        isDirty={isFireIdsDirty}
        discard={() => {
          dispatch(resetFireIds());

          if (formikRef) {
            formikRef.current.resetForm({ name: '', element: 0, subElement: 0 });
          }
        }}
        sendData={() => dispatch(updateFireIds(fireIds))}
      />
    </div>
  );
};

FireIdTab.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(FireIdTab);
